import request from 'lib/request';
import { Response } from 'lib/request/types';

// Define proper types for the filter options response
type TypeCurveHeadersResultItem = {
  basin: string;
  typeCurveAreaName: string;
  formation: string;
  uniqueTcId: string;
  dataLoadSource: string;
  status: string;
};

type TypeCurveHeadersUnpaginatedResponse = {
  results: TypeCurveHeadersResultItem[];
  totalCount: number;
};

// Define proper types for facets structure
interface FacetBucket {
  value: string;
  count: number;
}

interface FacetField {
  buckets: FacetBucket[];
}

interface TypeCurveFacets {
  basin?: FacetField;
  typeCurveAreaName?: FacetField;
  formation?: FacetField;
  uniqueTcId?: FacetField;
  dataLoadSource?: FacetField;
  status?: FacetField;
}

// Define proper types for filter options
interface FilterOption {
  label: string;
  value: string;
  helperText: string;
}

interface FilterOptions {
  basins: FilterOption[];
  typeCurveAreaNames: FilterOption[];
  formations: FilterOption[];
  uniqueTcIds: FilterOption[];
  dataLoadSources: FilterOption[];
  statuses: FilterOption[];
}

export default async function getFilterOptions(): Promise<{
  basins: string[];
  typeCurveAreaNames: string[];
  formations: string[];
  uniqueTcIds: string[];
  dataLoadSources: string[];
  statuses: string[];
}> {
  const response = await request.post<
    TypeCurveHeadersUnpaginatedResponse,
    Response<{ errors: { [key: string]: string[] } }>
  >(
    '/queries/type_curve_headers',
    {
      query: {
        paginated: false,
        active: true,
      },
    },
    new AbortController()
  );

  if (response.type === 'success') {
    const data = response.data;
    return {
      basins: getDistinctValuesFromResponse(data, 'basin'),
      typeCurveAreaNames: getDistinctValuesFromResponse(
        data,
        'typeCurveAreaName'
      ),
      formations: getDistinctValuesFromResponse(data, 'formation'),
      uniqueTcIds: getDistinctValuesFromResponse(data, 'uniqueTcId'),
      dataLoadSources: getDistinctValuesFromResponse(data, 'dataLoadSource'),
      statuses: getDistinctValuesFromResponse(data, 'status'),
    };
  } else {
    return {
      basins: [],
      typeCurveAreaNames: [],
      formations: [],
      uniqueTcIds: [],
      dataLoadSources: [],
      statuses: [],
    };
  }
}

function getDistinctValuesFromResponse(
  data: TypeCurveHeadersUnpaginatedResponse,
  field: keyof TypeCurveHeadersResultItem
): string[] {
  const result = new Set<string>();
  data.results.forEach((item: TypeCurveHeadersResultItem) => {
    if (item[field]) {
      result.add(item[field] as string);
    }
  });
  return Array.from(result).sort();
}

export function mapFacetsToOptions(
  facets: TypeCurveFacets | undefined
): FilterOptions {
  if (!facets) {
    return {
      basins: [],
      typeCurveAreaNames: [],
      formations: [],
      uniqueTcIds: [],
      dataLoadSources: [],
      statuses: [],
    };
  }

  return {
    basins: mapFacetFieldToOptions(facets.basin),
    typeCurveAreaNames: mapFacetFieldToOptions(facets.typeCurveAreaName),
    formations: mapFacetFieldToOptions(facets.formation),
    uniqueTcIds: mapFacetFieldToOptions(facets.uniqueTcId),
    dataLoadSources: mapFacetFieldToOptions(facets.dataLoadSource),
    statuses: mapFacetFieldToOptions(facets.status),
  };
}

function mapFacetFieldToOptions(
  facetField: FacetField | undefined
): FilterOption[] {
  if (!facetField?.buckets) {
    return [];
  }

  return facetField.buckets.map((bucket: FacetBucket) => ({
    label: bucket.value,
    value: bucket.value,
    helperText: `${bucket.count}`,
  }));
}

// Helper function to add "Select" option to filter options
export function addSelectOptionToFilters(filterOptions: string[]): string[] {
  // Filter out any empty or null values and sort
  const validOptions = filterOptions
    .filter((option) => option && option.trim() !== '')
    .sort();
  return ['Select', ...validOptions];
}

// Clean status value by removing underscores and replacing with spaces
export function cleanStatus(status: string): string {
  return status ? status.replace(/_/g, ' ') : '-';
}

// ─── API filter translation ───────────────────────────────────────────────────

import type { ColumnFiltersState } from '@tanstack/react-table';
import { LISTING_FILTER_IDS } from './type-curves-listing-filters';
import type { TypeCurvesListingFilters as ApiFilters } from './use-type-curves-listing';

function getVal(cf: ColumnFiltersState, id: string): unknown {
  return cf.find((f) => f.id === id)?.value;
}

export function buildApiFilters(cf: ColumnFiltersState): ApiFilters {
  const f: ApiFilters = {};

  // If `archived` is checked, omit `active`, which means show all rows (no
  // constraint).
  const archived = getVal(cf, LISTING_FILTER_IDS.archived) === true;
  if (!archived) f.active = true;

  const hasNewWells = getVal(cf, LISTING_FILTER_IDS.hasNewWells);
  if (typeof hasNewWells === 'string' && hasNewWells)
    f.hasNewWells = hasNewWells as 'since_created' | 'since_last_qc';

  const basin = getVal(cf, LISTING_FILTER_IDS.basin);
  if (typeof basin === 'string' && basin) f.basin = [basin];

  const formation = getVal(cf, LISTING_FILTER_IDS.formation);
  if (typeof formation === 'string' && formation) f.formation = [formation];

  const tcArea = getVal(cf, LISTING_FILTER_IDS.typeCurveAreaName);
  if (typeof tcArea === 'string' && tcArea) f.typeCurveArea = [tcArea];

  const dataSource = getVal(cf, LISTING_FILTER_IDS.dataLoadSource);
  if (typeof dataSource === 'string' && dataSource) f.dataSource = [dataSource];

  const status = getVal(cf, LISTING_FILTER_IDS.status);
  if (typeof status === 'string' && status) f.status = [status];

  const tcScenario = getVal(cf, LISTING_FILTER_IDS.typeCurveScenario);
  if (typeof tcScenario === 'string' && tcScenario)
    f.typeCurveScenarioIds = [tcScenario as unknown as number];

  const createdBy = getVal(cf, LISTING_FILTER_IDS.createdBy);
  if (typeof createdBy === 'string' && createdBy) f.createdBy = [createdBy];

  const devType = getVal(cf, LISTING_FILTER_IDS.developmentType);
  if (typeof devType === 'string' && devType) f.developmentType = devType;

  const basinAreaCount = getVal(cf, LISTING_FILTER_IDS.basinAreaCount) as
    { min?: number; max?: number } | undefined;
  if (basinAreaCount) f.basinAreaCount = basinAreaCount;

  const dcaScenario = getVal(cf, LISTING_FILTER_IDS.dcaScenario);
  if (typeof dcaScenario === 'string' && dcaScenario)
    f.dcaScenario = [dcaScenario];

  const scalingVars = getVal(cf, LISTING_FILTER_IDS.scalingVariables);
  if (Array.isArray(scalingVars) && scalingVars.length > 0)
    f.scalingVariables = scalingVars as string[];

  const promoted = getVal(cf, LISTING_FILTER_IDS.promoted);
  if (typeof promoted === 'string' && promoted)
    f.promoted = promoted === 'true';

  const promotedBy = getVal(cf, LISTING_FILTER_IDS.promotedBy);
  if (typeof promotedBy === 'string' && promotedBy) f.promotedBy = [promotedBy];

  const tasksComplete = getVal(cf, LISTING_FILTER_IDS.tasksComplete);
  if (typeof tasksComplete === 'string' && tasksComplete)
    f.tasksComplete = tasksComplete === 'true';

  const isDefault = getVal(cf, LISTING_FILTER_IDS.isDefault);
  if (typeof isDefault === 'string' && isDefault)
    f.isDefault = isDefault === 'true';
  // Inventory Workspaces view — the listing mock's own filter rail.
  const updatedBy = getVal(cf, LISTING_FILTER_IDS.updatedBy);
  if (typeof updatedBy === 'string' && updatedBy) f.updatedBy = [updatedBy];

  const verification = getVal(cf, LISTING_FILTER_IDS.verification);
  if (typeof verification === 'string' && verification)
    f.verification = verification as 'correct' | 'incorrect';

  const validation = getVal(cf, LISTING_FILTER_IDS.validation);
  if (typeof validation === 'string' && validation)
    f.validation = validation as 'valid' | 'warning' | 'invalid' | 'incomplete';

  const createdAt = getVal(cf, LISTING_FILTER_IDS.createdAt);
  if (Array.isArray(createdAt) && (createdAt[0] || createdAt[1])) {
    f.createdAt = {
      start: createdAt[0] ?? undefined,
      end: createdAt[1] ?? undefined,
    };
  }

  const uniqueTcId = getVal(cf, LISTING_FILTER_IDS.uniqueTcId);
  if (typeof uniqueTcId === 'string' && uniqueTcId.trim())
    f.uniqueTcId = uniqueTcId.trim();

  const updatedAt = getVal(cf, LISTING_FILTER_IDS.updatedAt);
  if (Array.isArray(updatedAt) && (updatedAt[0] || updatedAt[1])) {
    f.updatedAt = {
      start: updatedAt[0] ?? undefined,
      end: updatedAt[1] ?? undefined,
    };
  }

  return f;
}

export function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

/**
 * Produce a short hash string from tile URLs so source/layer IDs change when data changes.
 * @param {string[]} tiles - Tile URL strings to fingerprint.
 * @return {string} Base-36 string representing a deterministic hash of the tiles.
 */
export function tileFingerprint(tiles: string[]): string {
  let hash = 0;
  const str = tiles.join('|');
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}

// ─── Column ID → API sort_attribute mapping ───

const COLUMN_SORT_MAP: Record<string, string> = {
  uwi: 'uwi',
  name: 'name',
  leaseName: 'lease_name',
  wellNumber: 'well_number',
  currentOperator: 'current_operator',
  status: 'status',
  holeDirection: 'hole_direction',
  stateAbbreviation: 'state_abbreviation',
  trueVerticalDepthFeet: 'true_vertical_depth_feet',
  measuredDepthFeet: 'measured_depth',
  perforatedLateralLengthFt: 'perforated_lateral_length_ft',
  srpLandingZone: 'srp_landing_zone',
  parentChildStatus: 'parent_child_status',
  numberOfParents: 'number_of_parents',
  numberOfChildren: 'number_of_children',
  numberOfSiblings: 'number_of_siblings',
  closestAnyZoneTrueDistanceFt: 'closest_any_zone_true_distance_ft',
  closestAnyZoneHzDistanceFt: 'closest_any_zone_hz_distance_ft',
  closestAnyZoneVtDistanceFt: 'closest_any_zone_vt_distance_ft',
  closestAnyZoneOverlapPct: 'closest_any_zone_overlap_pct',
  secondClosestAnyZoneTrueDistanceFt:
    'second_closest_any_zone_true_distance_ft',
  secondClosestAnyZoneHzDistanceFt: 'second_closest_any_zone_hz_distance_ft',
  secondClosestAnyZoneVtDistanceFt: 'second_closest_any_zone_vt_distance_ft',
  secondClosestAnyZoneOverlapPct: 'second_closest_any_zone_overlap_pct',
  avgAnyZoneTrueDistanceFt: 'avg_any_zone_true_distance_ft',
  avgAnyZoneHzDistanceFt: 'avg_any_zone_hz_distance_ft',
  avgAnyZoneVtDistanceFt: 'avg_any_zone_vt_distance_ft',
  closestInZoneHzDistanceFt: 'closest_in_zone_hz_distance_ft',
  closestInZoneVtDistanceFt: 'closest_in_zone_vt_distance_ft',
  secondClosestInZoneTrueDistanceFt: 'second_closest_in_zone_true_distance_ft',
  secondClosestInZoneHzDistanceFt: 'second_closest_in_zone_hz_distance_ft',
  secondClosestInZoneVtDistanceFt: 'second_closest_in_zone_vt_distance_ft',
  secondClosestInZoneOverlapPct: 'second_closest_in_zone_overlap_pct',
  avgInZoneTrueDistanceFt: 'avg_in_zone_true_distance_ft',
  avgInZoneHzDistanceFt: 'avg_in_zone_hz_distance_ft',
  avgInZoneVtDistanceFt: 'avg_in_zone_vt_distance_ft',
  permitDate: 'permit_date',
  spudDate: 'spud_date',
  completionDate: 'completion_date',
  firstProductionDate: 'first_production_date',
  typeCurveFormation: 'type_curve_formation',
  wellQualifier: 'well_qualifier',
  oilEurBbl: 'oil_eur_bbl',
  gasEurMcf: 'gas_eur_mcf',
  waterEurBbl: 'water_eur_bbl',
  scaledOilEurBbl: 'scaled_oil_eur_bbl',
  scaledGasEurMcf: 'scaled_gas_eur_mcf',
  scaledWaterEurBbl: 'scaled_water_eur_bbl',
  oilEurPerFtBblFt: 'oil_eur_per_ft_bbl_ft',
  gasEurPerFtMcfFt: 'gas_eur_per_ft_mcf_ft',
  waterEurPerFtBblFt: 'water_eur_per_ft_bbl_ft',
  gorMcfBbl: 'gor_mcf_bbl',
  worBblBbl: 'wor_bbl_bbl',
  cgrBblMmcf: 'cgr_bbl_mmcf',
  wgrBblMmcf: 'wgr_bbl_mmcf',
  watercutPct: 'watercut_pct',
  generatedOn: 'generated_on',
  sourceId: 'source_id',
  businessId: 'business_id',
  proppantLbsPerFt: 'proppant_lbs_per_ft',
  fluidGalPerFt: 'fluid_gal_per_ft',
  fluidBblPerFt: 'fluid_bbl_per_ft',
  waterGalPerFt: 'water_gal_per_ft',
  waterBblPerFt: 'water_bbl_per_ft',
  acidGalPerFt: 'acid_gal_per_ft',
  acidBblPerFt: 'acid_bbl_per_ft',
  stageSpacingFt: 'stage_spacing_ft',
  proppantMeshSize: 'proppant_mesh_size',
  fluidSystem: 'fluid_system',
  jobDate: 'job_date',
};

export function mapColumnIdToSortAttribute(columnId: string): string {
  return COLUMN_SORT_MAP[columnId] ?? 'scaled_oil_eur_bbl';
}
